import { useSearchParams } from 'react-router-dom'

/**
 * Keeps the active tab in the URL as a query parameter so each tab has its own URL and
 * survives a page refresh / can be linked or bookmarked.
 *
 * The default tab is stored as a clean URL (no param). Switching tabs uses `replace` so it
 * doesn't pollute the browser history. Unknown param values fall back to the default.
 *
 * @param tabs       the valid tab keys
 * @param defaultTab the tab shown when the param is absent or invalid
 * @param paramName  the query-string key (default "tab")
 */
export function useTabParam<T extends string>(
    tabs: readonly T[],
    defaultTab: T,
    paramName = 'tab'
): [T, (tab: T) => void] {
    const [params, setParams] = useSearchParams()

    const raw = params.get(paramName)
    const current = raw && (tabs as readonly string[]).includes(raw) ? (raw as T) : defaultTab

    const setTab = (tab: T) => {
        const next = new URLSearchParams(params)
        if (tab === defaultTab) next.delete(paramName)
        else next.set(paramName, tab)
        setParams(next, { replace: true })
    }

    return [current, setTab]
}
